import { ChevronLeft, Cloud, Folder, FolderCheck, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, endpoints, type DriveListing } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { newId, useSettings } from '../../store/settings';
import { buttonClass, ghostButtonClass, Section } from './controls';

/** Browse OneDrive and add folders whose photos/videos join the slideshow. */
export function OneDrivePicker() {
  const { config, user, microsoft, refreshMicrosoft } = useSession();
  const folders = useSettings((s) => s.media.cloudFolders);
  const update = useSettings((s) => s.update);
  const [browsing, setBrowsing] = useState(false);

  if (!config?.features.microsoft) return null;

  const remove = (id: string) => update('media', { cloudFolders: folders.filter((f) => f.id !== id) });

  return (
    <Section title="OneDrive" description="Show photos and videos from folders in your OneDrive.">
      {!user ? (
        <p className="muted text-sm">Sign in (Settings → Account) to connect OneDrive.</p>
      ) : !microsoft?.connected ? (
        <button className={buttonClass} onClick={() => startMicrosoft('link')}>
          <Cloud className="h-4 w-4" aria-hidden /> Connect Microsoft account
        </button>
      ) : (
        <>
          <p className="muted text-xs">Connected as {microsoft.email ?? 'your Microsoft account'}.</p>
          {folders.length > 0 && (
            <ul className="space-y-2">
              {folders.map((f) => (
                <li key={f.id} className="flex items-center gap-3 rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
                  <FolderCheck className="h-4 w-4 shrink-0 text-[var(--accent)]" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <button onClick={() => remove(f.id)} aria-label={`Remove ${f.name}`} className="opacity-60 hover:opacity-100">
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {browsing ? (
            <Browser
              onPick={(folderId, name) => {
                if (!folders.some((f) => f.folderId === folderId)) {
                  update('media', { cloudFolders: [...folders, { id: newId(), provider: 'onedrive', folderId, name }] });
                }
                setBrowsing(false);
              }}
              onCancel={() => setBrowsing(false)}
              onDisconnected={() => void refreshMicrosoft()}
            />
          ) : (
            <button className={ghostButtonClass} onClick={() => setBrowsing(true)}>
              <Folder className="h-4 w-4" aria-hidden /> Choose a OneDrive folder
            </button>
          )}
        </>
      )}
    </Section>
  );
}

function Browser({
  onPick,
  onCancel,
  onDisconnected,
}: {
  onPick: (folderId: string, name: string) => void;
  onCancel: () => void;
  onDisconnected: () => void;
}) {
  const [path, setPath] = useState<Array<{ id: string | undefined; name: string }>>([{ id: undefined, name: 'OneDrive' }]);
  const [listing, setListing] = useState<DriveListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = path[path.length - 1]!;
  // Held in a ref: the parent passes a new function each render, which must not refetch.
  const disconnected = useRef(onDisconnected);
  disconnected.current = onDisconnected;

  useEffect(() => {
    let cancelled = false;
    setListing(null);
    setError(null);
    endpoints
      .driveChildren(current.id)
      .then((l) => !cancelled && setListing(l))
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.code === 'not_connected') disconnected.current();
        setError(e instanceof Error ? e.message : 'Could not open folder');
      });
    return () => {
      cancelled = true;
    };
  }, [current.id]);

  return (
    <div className="rounded-xl border border-[var(--border)] p-3">
      <div className="mb-2 flex items-center gap-2 text-sm">
        {path.length > 1 && (
          <button onClick={() => setPath(path.slice(0, -1))} aria-label="Up one folder" className="rounded p-1 hover:bg-[var(--border)]">
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
        )}
        <span className="truncate font-medium">{path.map((p) => p.name).join(' / ')}</span>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      {!listing && !error && (
        <p className="muted flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
        </p>
      )}
      {listing && (
        <>
          <ul className="max-h-64 space-y-0.5 overflow-y-auto">
            {listing.folders.map((f) => (
              <li key={f.id}>
                <button
                  onClick={() => setPath([...path, { id: f.id, name: f.name }])}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-[var(--border)]"
                >
                  <Folder className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="muted text-xs">{f.childCount}</span>
                </button>
              </li>
            ))}
            {listing.folders.length === 0 && <li className="muted px-2 text-sm">No sub-folders.</li>}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="muted text-xs">
              {listing.mediaCount} photo{listing.mediaCount === 1 ? '' : 's'}/video{listing.mediaCount === 1 ? '' : 's'} here
            </span>
            <div className="flex gap-2">
              <button className={ghostButtonClass} onClick={onCancel}>
                Cancel
              </button>
              <button
                className={buttonClass}
                disabled={!current.id || listing.mediaCount === 0}
                onClick={() => current.id && onPick(current.id, current.name)}
              >
                Use this folder
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
